import { useState, useContext, useEffect, useRef } from "react";

import classnames from "classnames";
import debounce from "lodash/debounce";
import PropTypes from "prop-types";

import { useAccount } from "components/hooks/accounts/useAccount";
import { useAlertBannerExperiment } from "components/hooks/experiments/useAlertBannerExperiment";
import { useTranslation } from "components/i18n/source";
import { AppContext, useBootstrap } from "components/store/source";
import {
  ConfirmationCodeSignInPopup,
  Hamburger,
  Link,
  LoginPopup,
  LoginOptionsPopup,
  SignUpPopup,
  LookUpPopup,
  Spacer,
  useHasMounted,
  useMedia,
} from "components/ui/source";
import { AlertBanner } from "components/ui/source/components/AlertBanner/AlertBanner";

import { useAllowedActions } from "hooks/actions/useAllowedActions";
import { ActionType } from "types/graphql";

import ActionsContainer from "./ActionsContainer";
import MenuModal from "./MenuModal";
import StickySearchBar from "./StickySearchBar";

import styles from "./Navbar.module.scss";

export function useNavbarRendered() {
  return (
    typeof window !== "undefined" &&
    !!document.querySelector("#navbar-component")
  );
}

function Navbar({
  disabled,
  sticky,
  fixed,
  children,
  page,
  hideActions,
  searchInput,
  isAirbnb,
}) {
  const { t } = useTranslation();
  const bootstrap = useBootstrap();
  const { account, mahalo } = bootstrap;
  const alertBannerExperiment = useAlertBannerExperiment();

  const {
    mobileMenuIsOpen,
    openMobileMenu,
    closeMobileMenu,
    confirmationCodePopupIsOpen,
    openConfirmationCodePopup,
    closeConfirmationCodePopup,
    openSignUpPopup,
    confirmationCodePopupProps,
  } = useContext(AppContext);
  const hasMounted = useHasMounted();
  const tablet = useMedia(["$tablet"], [true], false);
  const navbarRef = useRef(null);
  const accountActions = useAccount({ bootstrap })?.actions;
  const { getActionConfig } = useAllowedActions({
    allowedActions: accountActions,
  });

  let scrollHandled = false;

  const [showLoginActionContainer, setShowLoginActionContainer] =
    useState(true);
  const [showLoginModals, setShowLoginModals] = useState(true);

  useEffect(() => {
    const accountEditAction = getActionConfig(ActionType.AccountEdit);
    const accountViewAction = getActionConfig(ActionType.AccountView);
    const showLogin = !(
      accountEditAction.isActionAllowed && accountViewAction.isActionAllowed
    );

    setShowLoginActionContainer(showLogin);
    setShowLoginModals(showLogin);
  }, [accountActions, getActionConfig]);

  // Add shadow to nav div on scroll
  useEffect(() => {
    const updateScrollY = debounce(() => {
      // Update container class directly to avoid rerendering
      // Not to update classList too frequently, store current handling state
      if (window.scrollY == 0) {
        navbarRef.current?.classList.remove(styles.scrolled);
        scrollHandled = false; // eslint-disable-line
      } else if (!scrollHandled) {
        navbarRef.current?.classList.add(styles.scrolled);
        scrollHandled = true;
      }
    }, 0);

    document.addEventListener("scroll", updateScrollY, { passive: true });

    updateScrollY();

    return () => {
      updateScrollY.cancel();
      document.removeEventListener("scroll", updateScrollY);
    };
  }, []);

  function Logo() {
    return (
      <img
        className={styles.brand_image}
        src="https://images.sonder.com/image/upload/q_100,f_auto,e_sharpen:100,dpr_2.0/v1737485839/sondercom/partners/sonderByMarriottLogo.png"
        alt={t("sonder")}
        fetchPriority="high"
      />
    );
  }

  function Actions() {
    let actionProps = {
      account,
      isAirbnb,
      confirmationPopupClosed: !confirmationCodePopupIsOpen,
      openConfirmationPopup: openConfirmationCodePopup,
      closeConfirmationPopup: closeConfirmationCodePopup,
      showLoginButton: showLoginActionContainer,
    };

    if (tablet) {
      actionProps = { ...actionProps, orientation: "column", screen: "mobile" };
    }

    return <ActionsContainer {...actionProps} />;
  }

  function AccountModals() {
    if (account.anonymous) {
      return (
        <>
          <LoginPopup />
          <LoginOptionsPopup />
          <SignUpPopup />
          <LookUpPopup />
        </>
      );
    }
    if (showLoginModals) {
      return (
        <>
          <LoginOptionsPopup />
          <LoginPopup />
        </>
      );
    }
    return null;
  }

  // Mahalo doesn't render the navbar so that users can't freely navigate
  // on inside the webview, but it does need to render the account modals.
  if (mahalo) {
    return (
      <div id="navbar-component">
        <AccountModals />
      </div>
    );
  }

  return (
    <div
      className={classnames(
        styles.wrapper,
        sticky && styles.sticky,
        fixed && styles.fixed,
      )}
      ref={navbarRef}
    >
      <AlertBanner
        heading={alertBannerExperiment?.payload?.heading}
        description={alertBannerExperiment?.payload?.description}
        startTimestamp={alertBannerExperiment?.payload?.startTimestamp}
        endTimestamp={alertBannerExperiment?.payload?.endTimestamp}
      />
      <nav
        id="navbar-component"
        className={classnames(styles.container, styles[`page__${page}`])}
      >
        {disabled || isAirbnb ? (
          <Logo />
        ) : (
          <Link className={styles.logo_link} href="/">
            <Logo />
          </Link>
        )}
        {page === "home" && (
          <StickySearchBar searchInput={searchInput}>
            {children}
          </StickySearchBar>
        )}
        {page !== "home" && <Spacer />}
        <div className={styles.actions}>
          {hideActions || !hasMounted ? null : tablet ? (
            <>
              <Hamburger onClick={openMobileMenu} isOpen={mobileMenuIsOpen} />
            </>
          ) : (
            <>
              <Actions />
            </>
          )}
        </div>
      </nav>
      {tablet && (
        <MenuModal
          account={account}
          isOpen={mobileMenuIsOpen}
          onClose={closeMobileMenu}
          actions={<Actions />}
        />
      )}
      <ConfirmationCodeSignInPopup
        hidden={!confirmationCodePopupIsOpen}
        closeConfirmationPopup={closeConfirmationCodePopup}
        openSignUpPopup={() => openSignUpPopup({ source: "Check In" })}
        confirmationCodePopupProps={confirmationCodePopupProps}
      />
      <AccountModals />
    </div>
  );
}

Navbar.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  searchInput: PropTypes.node,
  disabled: PropTypes.bool,
  sticky: PropTypes.bool,
  fixed: PropTypes.bool,
  hideActions: PropTypes.bool,
  page: PropTypes.oneOf(["any", "home", "search"]),
  isAirbnb: PropTypes.bool,
};

Navbar.defaultProps = {
  children: null,
  searchInput: null,
  disabled: false,
  sticky: false,
  fixed: false,
  hideActions: false,
  isAirbnb: false,
  page: "any",
};

export default Navbar;
